import React from 'react';
import {spinner} from '../styles/style';
import {toJS} from 'mobx';
import {DiagramType} from '../stores/DashboardStore';
import BarChart, {BarChartData} from './charts/BarChart';
import {stylesheet} from 'typestyle';
import {KpiPackage, VisibleAttribute} from '../types/Chart';
import AreaChart from './charts/AreaChart';
import {inject, Stores} from '../inject';
import {I18n} from '../i18n/i18n';
import {DashboardLayoutSlotStyle} from './DashboardLayout';
import {moneyFormat} from '../utility/FormatValueUtils';
import _ from 'lodash';
import {LightGreyColor, BlackColor, WhiteColor} from '../styles/colors';

const OUTER_PADDING = 10;
const INNER_PADDING = 20;
const TITLE_HEIGHT = 30;

interface DashboardChartContainerProps {
    isLoading: boolean;
    chartType?: 'barChart' | 'areaChart';
    data: BarChartData[];
    title: string | React.ReactNode;
    onTitleClick?: (diagram: DiagramType) => void;
    visibleAttributes: VisibleAttribute[];
    kpiPackageToString?: (kpiRequestInfo: KpiPackage, payload: BarChartData) => string | React.ReactNode;
    onBarClick?: (payload: BarChartData) => void;
    vertical: boolean;
    titleIsClickable?: boolean;
    style: DashboardLayoutSlotStyle;
}

class DashboardChartContainer extends React.Component<DashboardChartContainerProps & Stores<'store'>> {
    private newDataIsUnequalToOldData(newData: BarChartData[]) {
        if (newData.length !== this.props.data.length) {
            return true;
        }
        return newData.some((newDataEntry, index) => {
            if (!_.isEqual(newDataEntry, this.props.data[index])) {
                return true;
            }
            return newDataEntry.data.some((dataPackage, dataIndex) => !_.isEqual(dataPackage, this.props.data[index].data[dataIndex]));
        });
    }

    public shouldComponentUpdate(nextProps: DashboardChartContainerProps) {
        if (nextProps.isLoading !== this.props.isLoading) {
            return true;
        }
        return this.newDataIsUnequalToOldData(nextProps.data) || !_.isEqual(nextProps.style, this.props.style);
    }

    private renderChart(width: number, height: number): React.ReactNode {
        const {selectedChannel} = this.props.store.channelStore;
        if (!this.props.chartType || this.props.chartType === 'barChart') {
            return (
                <BarChart
                    allowBarAnimation={false}
                    currencyFormatting={moneyFormat(selectedChannel)}
                    data={toJS(this.props.data)}
                    onBarClick={this.props.onBarClick ? this.props.onBarClick : () => {}}
                    visibleAttribute={this.props.visibleAttributes}
                    width={width}
                    height={height}
                    vertical={this.props.vertical}
                    kpiPackageToString={this.props.kpiPackageToString}
                />
            );
        } else {
            return (
                <AreaChart
                    allowAnimation={false}
                    currencyFormatting={moneyFormat(selectedChannel)}
                    data={toJS(this.props.data)}
                    visibleAttribute={this.props.visibleAttributes}
                    width={width}
                    height={height}
                    onBarClick={this.props.onBarClick ? this.props.onBarClick : () => {}}
                    kpiPackageToString={this.props.kpiPackageToString}
                />
            );
        }
    }

    public render() {
        const {isLoading, title, titleIsClickable = true} = this.props;

        const hasData = this.props.data.length > 0;
        const onTitleClick = () => (this.props.onTitleClick ? this.props.onTitleClick(DiagramType.TOP_TEN_SEARCHTERMS) : null);
        const {width, height} = this.props.style;

        const bodyWidth = width - 2 * OUTER_PADDING - 2 * INNER_PADDING;
        const bodyHeight = height - 2 * OUTER_PADDING - 3 * INNER_PADDING - TITLE_HEIGHT;
        let body;
        if (isLoading) {
            body = (
                <div style={{width, height, textAlign: 'center'}}>
                    <div className={spinner(50)} />
                </div>
            );
        } else if (!hasData) {
            body = (
                <div className={diagramCss.noDiagramPlaceholder} style={{lineHeight: bodyHeight + 'px'}}>
                    <I18n i18nKey="data.notAvailable" />
                </div>
            );
        } else {
            body = this.renderChart(bodyWidth, bodyHeight);
        }
        const titleLinkClass = titleIsClickable && hasData ? '' : diagramCss.notClickableLink;

        const style = {...this.props.style};

        return (
            <div className={diagramCss.container} style={style}>
                <div className={diagramCss.containerInner}>
                    <div className={diagramCss.title} onClick={hasData ? onTitleClick : undefined}>
                        <a className={titleLinkClass} href="javascript:void(0)">
                            {title}
                        </a>
                    </div>
                    {body}
                </div>
            </div>
        );
    }
}

export default inject('store')(DashboardChartContainer);

const diagramCss = stylesheet({
    notClickableLink: {
        cursor: 'default',
        $nest: {
            '&:hover': {
                textDecoration: 'none',
            },
        },
    },
    noDiagramPlaceholder: {
        textAlign: 'center',
    },
    container: {
        position: 'absolute',
    },
    containerInner: {
        position: 'absolute',
        top: OUTER_PADDING,
        right: OUTER_PADDING,
        bottom: OUTER_PADDING,
        left: OUTER_PADDING,
        border: '1px solid ' + LightGreyColor,
        padding: INNER_PADDING - 1, // - 1 wg. border
        paddingTop: INNER_PADDING - 1,
        backgroundColor: WhiteColor,
        borderRadius: 3,
    },
    title: {
        height: TITLE_HEIGHT,
        marginBottom: INNER_PADDING,
        $nest: {
            '& a': {
                maxWidth: '90%',
                display: 'inline-block',
                color: BlackColor,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                fontWeight: 300,
                fontSize: '120%',
            },
            '&:hover a': {
                whiteSpace: 'initial',
            },
        },
    },
});



// WEBPACK FOOTER //
// ./src/views/DashboardChartContainer.tsx